/**
 * One guided experiment, as admin sees it — reached from its row in the
 * Simulations list (/simulations/experiments/:id).
 *
 * The procedure is shown, not edited: it is mastered in the repo catalogue
 * (`@padcanvas/shared/guidedExperimentCatalog.ts`), which zitmus also builds
 * its indexed pages from. What admin changes is curation — status, chapters,
 * who it is for — through the same editor a simulation uses.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@shared-ui/components';
import { findSubstance, formatAmount, type GuidedStep } from '@padcanvas/shared';
import { chemistryLabApi, type GuidedExperimentAdmin } from '../../api/chemistryLabApi';
import { CurationEditor } from './CurationEditor';
import './GuidedExperimentPage.css';

const VESSEL_LABELS: Record<string, string> = {
  flask: 'Flask', beaker: 'Beaker', 'round-flask': 'Round-bottom flask', 'test-tube': 'Test tube',
};

const QUANTITY_LABELS: Record<string, string> = {
  moles: 'moles of', 'limiting-reagent': 'which reagent runs out', 'leftover-g': 'mass left of',
  'yield-mol': 'moles made of', 'yield-g': 'mass made of', 'yield-volume-l': 'volume made of',
  concentration: 'concentration of',
};

/** What a step waits for, in words. */
function describe(expect: GuidedStep['expect'], vessels: string[]): string {
  const reagent = (id: string) => findSubstance(id)?.displayName ?? id;
  switch (expect.kind) {
    case 'add-substance': {
      const substance = findSubstance(expect.substanceId);
      const amount = substance && expect.qty ? ` — ${formatAmount(substance, expect.qty)} in all` : '';
      return `Add ${reagent(expect.substanceId)}${amount}`;
    }
    case 'dip-test': return 'Dip the pH paper';
    case 'dip-rod': return `Dip the glass rod in ${reagent(expect.substanceId)}`;
    case 'attach-burner': return 'Put a flame under it';
    case 'heat': return `Heat it until ${expect.toBand}`;
    case 'connect-pipe': return `Run a delivery tube to ${vessels[expect.toContainer] ?? `vessel ${expect.toContainer + 1}`}`;
    case 'swirl': return 'Swirl it';
    case 'observe': return 'Watch — nothing to do';
    case 'answer-quantity':
      return `Asks for the ${QUANTITY_LABELS[expect.quantity] ?? expect.quantity}${expect.of ? ` ${expect.of}` : ''}`;
  }
}

export function GuidedExperimentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [experiment, setExperiment] = useState<GuidedExperimentAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    chemistryLabApi.listExperiments()
      .then(all => {
        const found = all.find(e => e.id === id);
        if (found) setExperiment(found);
        else setError(`No guided experiment "${id}".`);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Could not load the experiment.'));
  }, [id]);

  const back = <Button variant="secondary" onClick={() => navigate('/simulations')}>← Simulations</Button>;
  if (error) return <div className="gx">{back}<p className="gx-error">{error}</p></div>;
  if (!experiment) return <div className="gx"><p className="gx-muted">Loading…</p></div>;

  const vessels = experiment.bench.map(v => v.label || VESSEL_LABELS[v.vesselType]);

  return (
    <div className="gx">
      <div className="gx-bar">
        {back}
        <Button onClick={() => setEditing(true)}>Edit status, chapters &amp; audience</Button>
      </div>

      <header className="gx-head">
        <span className="gx-glyph" aria-hidden="true">{experiment.glyph}</span>
        <div>
          <div className="gx-tags">
            <span className={`gx-status gx-status-${experiment.status}`}>{experiment.status === 'live' ? 'Live' : 'Draft'}</span>
            {experiment.mode === 'quantitative' && <span className="gx-tag">With amounts</span>}
            {experiment.chapterNames.map(name => <span key={name} className="gx-tag">{name}</span>)}
          </div>
          <h1>{experiment.title}</h1>
          <p className="gx-tagline">{experiment.tagline}</p>
        </div>
      </header>

      <p className="gx-muted">
        The procedure comes from <code>guidedExperimentCatalog.ts</code> in the repo, which zitmus also
        builds its pages from. Change it there and re-seed.
      </p>

      <section className="gx-section">
        <p>{experiment.description}</p>
        {experiment.equation && <p className="gx-equation">{experiment.equation}</p>}
      </section>

      <section className="gx-section">
        <h3>The bench it starts on</h3>
        <ol className="gx-list">
          {experiment.bench.map((v, i) => <li key={i}>{vessels[i]} <span className="gx-muted">({VESSEL_LABELS[v.vesselType]})</span></li>)}
        </ol>
      </section>

      <section className="gx-section">
        <h3>The steps</h3>
        <ol className="gx-steps">
          {experiment.steps.map((step, i) => (
            <li key={i}>
              <p className="gx-instruction">{step.instruction}</p>
              <p className="gx-muted">{vessels[step.container]} · {describe(step.expect, vessels)}</p>
              {step.observe && <p className="gx-observe">{step.observe}</p>}
            </li>
          ))}
        </ol>
      </section>

      <section className="gx-section">
        <h3>What it shows</h3>
        <p>{experiment.conclusion}</p>
      </section>

      <CurationEditor
        entry={experiment}
        isOpen={editing}
        onClose={() => setEditing(false)}
        save={chemistryLabApi.curateExperiment}
        onSaved={setExperiment}
      />
    </div>
  );
}
